import { FC, useEffect, useRef } from 'react';

import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useDialog } from 'hooks/useDialog';
import { useGetArticleData } from 'hooks/useGetArticleData';
import { useGetProgressData } from 'hooks/useGetProgressData';
import { usePageTitle } from 'hooks/usePageTitle';
import { useRemoveRubyTags } from 'hooks/useRemoveRubyTags';

import { Button } from 'components/parts/Button';
import BgParkingLot01 from 'images/area/bg_parking-lot01.svg';
import BgParkingLot02 from 'images/area/bg_parking-lot02.svg';
import BgParkingLot03 from 'images/area/bg_parking-lot03.svg';
import BgReceivingPlace from 'images/area/bg_receiving-place.svg';
import BgRecommended from 'images/area/bg_recommended.svg';
import BgStoresOfferTips from 'images/area/bg_stores-offer-tips.svg';
import Step01_main from 'images/area/step01.svg';
import Step01Clear_main from 'images/area/step01_clear.svg';
import Step02_main from 'images/area/step02.svg';
import Step02Clear_main from 'images/area/step02_clear.svg';
import Step01_alt from 'images/area/step01_alt.svg';
import Step01Clear_alt from 'images/area/step01_alt_clear.svg';
import Step02_alt from 'images/area/step02_alt.svg';
import Step02Clear_alt from 'images/area/step02_alt_clear.svg';
import AddressReceivingPlace from 'images/icon/address_receiving-place.svg';
import AddressStoresOfferTips from 'images/icon/address_stores-offer-tips.svg';
import Check from 'images/icon/check.svg';
import Clue1 from 'images/icon/clue1.svg';
import Clue2 from 'images/icon/clue2.svg';
import Clue3 from 'images/icon/clue3.svg';
import DayReceivingPlace from 'images/icon/day_receiving-place.svg';
import ActiveIcon from 'images/icon/difficulty-level_active.svg';
import DeactiveIcon from 'images/icon/difficulty-level_deactive.svg';
import Expansion from 'images/icon/expansion.svg';
import mapReceivingPlace from 'images/icon/map_receiving-place.svg';
import mapStoresOfferTips from 'images/icon/map_stores-offer-tips.svg';
import TagStoresOfferTips from 'images/icon/tag_stores-offer-tips.svg';
import TelReceivingPlace from 'images/icon/tel_receiving-place.svg';
import TelStoresOfferTips from 'images/icon/tel_stores-offer-tips.svg';
import TimeIcon from 'images/icon/time.svg';

import styles from './AreaDetail.module.scss';
import { MapDialogView } from './components/MapDialogView/MapDialogView';
import { MissionSection } from './components/MissionSection/MissionSection';
import { cx } from 'utils/cx';
import { useNews } from 'hooks/useNews';
import { NewsLinkButton } from 'components/pages/AreaDetail/components/NewsLinkButton/NewsLinkButton';

function useFlagImages(group: string) {
  if (group === '浜通り地方') {
    return {
      Step01: Step01_alt,
      Step01Clear: Step01Clear_alt,
      Step02: Step02_alt,
      Step02Clear: Step02Clear_alt,
    };
  } else {
    return {
      Step01: Step01_main,
      Step01Clear: Step01Clear_main,
      Step02: Step02_main,
      Step02Clear: Step02Clear_main,
    };
  }
}

export const AreaDetail: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const { data: progressData } = useGetProgressData();
  const article = useGetArticleData(id || '');
  const areaStatus =
    progressData?.areaStatusList.find(area => String(area.areaNo) === article?.id) || undefined;
  const { isOpen, handleOpen, handleClose } = useDialog();

  const { Step01, Step01Clear, Step02, Step02Clear } = useFlagImages(article?.group || '');

  const isHamadori = article?.group === '浜通り地方';

  usePageTitle(useRemoveRubyTags(article?.name));

  const newsData = useNews();
  const targetedNews = newsData?.find(news => news.relatedSpot.includes(`area${id}`));

  //アンカーリンクつきで遷移してきたときにミッションにスクロール
  useEffect(() => {
    if (!location.hash || !article?.mission?.length) return;
    const targetId = decodeURIComponent(location.hash.replace('#', ''));
    requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    });
  }, [article?.id, article?.mission?.length, location.hash]);

  // ヒントをもらいに行くスクロール
  const storesOfferTips = useRef<HTMLDivElement>(null);

  // ヒント提供店までスクロール
  const navigateScroll = () => {
    if (storesOfferTips.current) {
      storesOfferTips.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  /**
   * 「手がかりのありか」
   */
  const hints = [
    article?.clueHints?.clueHint1,
    article?.clueHints?.clueHint2,
    article?.clueHints?.clueHint3,
  ].filter(hint => hint !== undefined);

  const clue = [areaStatus?.foundClue_1, areaStatus?.foundClue_2, areaStatus?.foundClue_3].slice(
    0,
    article?.clueCount || 3
  );

  /**
   * 手がかりが全て見つかっているかを判定する関数
   * @returns {boolean} - 手がかりが全て見つかっている場合はtrue、それ以外の場合はfalse
   */
  function allCluesFound() {
    return clue.every(value => value);
  }
  /**
   * 謎をクリアしているか？
   */
  const isCompleted = areaStatus?.completed;

  /**
   * `article?.clueMap`のファイル名を出力する
   * @returns {string} - 生成されたファイル名
   */
  function getClueMapFileName() {
    if (article && article.id) {
      return `${String(article.id).padStart(3, '0')}_clue_map.webp`;
    }
    return '';
  }

  /**
   * 手がかりの状態に基づいてファイル名を生成する関数
   *
   * @returns {string} - 生成されたファイル名
   * @throws {Error} - 手がかりの配列の長さが0,1,2,3以外の場合にエラーを投げる
   *
   * @example
   * const clue = [true, false, true];
   * console.log(getFileName(1, clue));  // "001_nazo_1-3.webp"が出力される
   */
  function getNanoFileName() {
    // 各値がtrueかどうかを調べて、その結果を使って何番目の手がかりが見つかったかを判断する
    // 取得済みヒントのインデックスを文字列に変換
    // 例: [true, false, true] → "1-3"
    // 例: [true, true, false] → "1-2"
    // 例: [false, false, false] → ""
    let fileName = clue
      .map((c, i) => (c ? i + 1 : ''))
      .filter(Boolean)
      .join('-');

    // ヒント数に応じて完了時のファイル名を決定
    // 例: clueCount=2 → completedPattern="1-2"
    // 例: clueCount=3 → completedPattern="1-2-3"
    const clueCount = article?.clueCount || 3;
    const completedPattern = Array.from({ length: clueCount }, (_, i) => i + 1).join('-');

    // 全ヒント取得時は "comp"、未取得時は "0" に変換
    // 例: fileName="1-2-3" かつ clueCount=3 → "comp"
    // 例: fileName="1-2" かつ clueCount=2 → "comp"
    // 例: fileName="" → "0"
    if (fileName === completedPattern) {
      fileName = 'comp';
    } else if (fileName === '') {
      fileName = '0';
    }

    // ファイル名をパディングし、codeと合体させる
    // 例: id=1, fileName="1-2" → "001_nazo_1-2.webp"
    // 例: id=7, fileName="comp" → "007_nazo_comp.webp"
    // 例: id=3, fileName="0" → "003_nazo_0.webp"
    if (article && article.id) {
      return `${String(article.id).padStart(3, '0')}_nazo_${fileName}.webp`;
    }
    return '';
  }

  function getNazoImagePath() {
    if (!article || !article.id) return null;

    // 画像が存在しない場合にエラーを投げて処理を続行
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const imagePath = require(`components/pages/Area/images/${getNanoFileName()}`);
      return imagePath ? imagePath : null;
    } catch (error) {
      return null;
    }
  }

  function getRecommendedFileName(index: number) {
    if (article && article.id) {
      return `${String(article.id).padStart(3, '0')}_recommended_${String(index + 1).padStart(
        2,
        '0'
      )}.webp`;
    }
    return '';
  }

  const getDifficultyIndicator = (level: string | undefined) => {
    const levelNumber = Number(level);
    switch (levelNumber) {
      case 1:
        return (
          <div className={styles.difficultyLevel}>
            <img src={ActiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <img src={DeactiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <img src={DeactiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <span className={styles.sronly}>難易度1</span>
          </div>
        );
      case 2:
        return (
          <div className={styles.difficultyLevel}>
            <img src={ActiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <img src={ActiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <img src={DeactiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <span className={styles.sronly}>難易度2</span>
          </div>
        );
      case 3:
        return (
          <div className={styles.difficultyLevel}>
            <img src={ActiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <img src={ActiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <img src={ActiveIcon} alt="" className={styles.difficultyLevelIcon} />
            <span className={styles.sronly}>難易度3</span>
          </div>
        );
      default:
        return 'Invalid difficulty level';
    }
  };

  const renderClueChain = () => {
    return (
      <span className={styles.stepTextClueChain}>
        <img src={Clue1} alt="手がかり1" className={styles.stepTextClue} />
        {(article?.clueCount || 3) >= 2 && (
          <>
            ・
            <img src={Clue2} alt="手がかり2" className={styles.stepTextClue} />
          </>
        )}
        {(article?.clueCount || 3) >= 3 && (
          <>
            ・
            <img src={Clue3} alt="手がかり3" className={styles.stepTextClue} />
          </>
        )}
      </span>
    );
  };

  const [explorePossibleTimes, explorePossibleTimesNote] = (
    article?.explorePossibleTimes ?? ''
  ).split('※');

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <div className={styles.headerBox}>
          {targetedNews && (
            <div className={styles.newsBar}>
              <NewsLinkButton newsNumber={targetedNews.number} />
            </div>
          )}
          <div className={styles.header}>
            <div className={styles.headerFirst}>
              <p className={cx(styles.no, isHamadori && styles['--hamadori'])}>{article?.id}</p>
            </div>
            <div className={styles.headerSecond}>
              <h1
                dangerouslySetInnerHTML={{ __html: String(article?.name) }}
                className={styles.title}
              />
              <div className={styles.areaAttributes}>
                <div className={styles.difficultyLevelWrap}>
                  <p className={styles.difficultyLevelWrapText}>難易度</p>
                  {getDifficultyIndicator(article?.difficultyLevel)}
                </div>
                <div className={styles.explorePossibleTime}>
                  <p className={styles.explorePossibleTimeText}>
                    探索可能時間 {explorePossibleTimes.replace(/：/g, ':').replace('〜', '~')}
                  </p>
                </div>
              </div>
              {explorePossibleTimesNote && (
                <>
                  <p className={styles.explorePossibleTimeNote}>{'※' + explorePossibleTimesNote}</p>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.step}>
            {isCompleted ? (
              <img
                src={require(`images/character/chara-map.webp`)}
                alt=""
                className={styles.charaMap}
              />
            ) : (
              <img
                src={require(`images/character/chara-map.webp`)}
                alt=""
                className={styles.charaMap}
              />
            )}
            <div className={styles.stepInner}>
              {allCluesFound() ? (
                <img src={Step01Clear} alt="STEP01 チェック" className={styles.stepLabel} />
              ) : (
                <img src={Step01} alt="STEP01" className={styles.stepLabel} />
              )}
              <h2 className={styles.stepHeading}>手がかりを手に入れよう</h2>

              {/* ヒントが２つの場合はガイドが1-2まで */}
              <div className={styles.stepText}>
                <p>まずは手がかりのありかの情報をもとに手がかり{renderClueChain()}を集めよう!</p>
                <p>3つの手がかりを集めると、STEP02の謎が完成して解けるようになるよ！</p>
                <p>手がかり{renderClueChain()}は好きな順番でめぐろう！</p>
              </div>
              <div className={styles.stepClueMap}>
                <button onClick={() => handleOpen()} className={styles.stepClueMapButton}>
                  {article?.clueMap && (
                    <>
                      <img
                        src={
                          getClueMapFileName() &&
                          require(`components/pages/Area/images/${getClueMapFileName()}`)
                        }
                        alt=""
                        className={styles.stepClueMapImage}
                      />
                      <span className={styles.clueMapLabel}>
                        <img src={Expansion} alt="" className={styles.clueMapLabelIcon} />
                        <span className={styles.clueMapLabelText}>地図を拡大</span>
                      </span>
                    </>
                  )}
                </button>
              </div>
              <ol className={styles.clueList}>
                <li className={styles.clueListItem}>
                  <div className={styles.clueListImageWrap}>
                    {clue[0] ? (
                      <img
                        src={Check}
                        alt=""
                        className={[styles.clueImage, styles.check1].join(' ')}
                      />
                    ) : (
                      <img
                        src={Clue1}
                        alt=""
                        className={[styles.clueImage, styles.check].join(' ')}
                      />
                    )}
                  </div>
                  <div className={styles.clueListTextWrap}>
                    {hints[0] && (
                      <p
                        className={styles.clueListText}
                        data-check={clue[0] ? 'true' : 'false'}
                        dangerouslySetInnerHTML={{ __html: String(hints[0]) }}
                      />
                    )}
                  </div>
                </li>
                {/* ヒントが２つ以上ある場合のみ２つ目のヒントを表示する */}
                {(article?.clueCount || 3) >= 2 && (
                  <li className={styles.clueListItem}>
                    <div className={styles.clueListImageWrap}>
                      {clue[1] ? (
                        <img
                          src={Check}
                          alt=""
                          className={[styles.clueImage, styles.check2].join(' ')}
                        />
                      ) : (
                        <img
                          src={Clue2}
                          alt=""
                          className={[styles.clueImage, styles.check].join(' ')}
                        />
                      )}
                    </div>
                    <div className={styles.clueListTextWrap}>
                      {hints[1] && (
                        <p
                          className={styles.clueListText}
                          data-check={clue[1] ? 'true' : 'false'}
                          dangerouslySetInnerHTML={{ __html: String(hints[1]) }}
                        />
                      )}
                    </div>
                  </li>
                )}

                {/* ヒントが３つある場合のみ３つ目のヒントを表示する */}
                {(article?.clueCount || 3) >= 3 && (
                  <li className={styles.clueListItem}>
                    <div className={styles.clueListImageWrap}>
                      {clue[2] ? (
                        <img
                          src={Check}
                          alt=""
                          className={[styles.clueImage, styles.check3].join(' ')}
                        />
                      ) : (
                        <img
                          src={Clue3}
                          alt=""
                          className={[styles.clueImage, styles.check].join(' ')}
                        />
                      )}
                    </div>
                    <div className={styles.clueListTextWrap}>
                      {hints[2] && (
                        <p
                          className={styles.clueListText}
                          data-check={clue[2] ? 'true' : 'false'}
                          dangerouslySetInnerHTML={{ __html: String(hints[2]) }}
                        />
                      )}
                    </div>
                  </li>
                )}
              </ol>
              <p className={styles.stepText}>
                実際に手がかりを見つけたら、手がかりの合言葉を入力しよう！
              </p>
              <div className={styles.stepClueButton}>
                <Button
                  text="合言葉を入力"
                  styleType="primary"
                  handleClick={() => navigate(`/area/clue/${article?.id}`)}
                  addClass={[styles.spotButton]}
                  disabled={allCluesFound()}
                />
              </div>
            </div>
          </div>

          <div className={styles.step}>
            <div className={styles.stepInner}>
              {isCompleted ? (
                <img src={Step02Clear} alt="STEP02 チェック" className={styles.stepLabel} />
              ) : (
                <img src={Step02} alt="STEP02" className={styles.stepLabel} />
              )}
              <h2 className={styles.stepHeading}>謎を解いて宝を見つけよう</h2>
              <p className={styles.stepText}>
                手がかりの合言葉を入力すると、謎が解けるようになるよ！
              </p>

              {/* ナゾの画像 */}
              <div className={styles.nazo}>
                {article?.nazo && article?.nazo[0] && getNazoImagePath() && (
                  <img src={getNazoImagePath()} alt="" className={styles.nazoImage} />
                )}
              </div>

              <ol className={styles.nazoClueList}>
                <li className={styles.nazoClueItem}>
                  {clue[0] ? (
                    <img
                      src={Check}
                      alt=""
                      className={[styles.clueImage, styles.check1].join(' ')}
                    />
                  ) : (
                    <img
                      src={Clue1}
                      alt=""
                      className={[styles.clueImage, styles.checkSmall].join(' ')}
                    />
                  )}
                </li>
                {/* ヒントが2つ以上ある場合のみ２つ目のヒントマークを表示 */}
                {(article?.clueCount || 3) >= 2 && (
                  <li className={styles.nazoClueItem}>
                    {clue[1] ? (
                      <img
                        src={Check}
                        alt=""
                        className={[styles.clueImage, styles.check2].join(' ')}
                      />
                    ) : (
                      <img
                        src={Clue2}
                        alt=""
                        className={[styles.clueImage, styles.checkSmall].join(' ')}
                      />
                    )}
                  </li>
                )}

                {/* ヒントが3つある場合のみ３つ目のヒントマークを表示 */}
                {(article?.clueCount || 3) >= 3 && (
                  <li className={styles.nazoClueItem}>
                    {clue[2] ? (
                      <img
                        src={Check}
                        alt=""
                        className={[styles.clueImage, styles.check3].join(' ')}
                      />
                    ) : (
                      <img
                        src={Clue3}
                        alt=""
                        className={[styles.clueImage, styles.checkSmall].join(' ')}
                      />
                    )}
                  </li>
                )}
              </ol>
              <p className={styles.stepText}>
                謎を解くと空に導く宝のありかがわかるよ！宝箱を発見したらキーワードを入力しよう。
              </p>
              <div className={styles.stepKeywordButton}>
                <Button
                  text="キーワードを入力"
                  styleType="primary"
                  handleClick={() => navigate(`/area/answer/${article?.id}`)}
                  addClass={[styles.spotButton]}
                  disabled={isCompleted}
                />
                {!isCompleted && (
                  <button className={styles.findHint} type="button" onClick={navigateScroll}>
                    ヒントをもらいに行く
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {article?.mission && article.mission.length > 0 && (
          <MissionSection article={article} progressData={progressData} />
        )}
        <div className={styles.foot}>
          <div className={styles.receivingPlace}>
            <h2 className={[styles.footHeading, styles.receivingPlaceHeading].join(' ')}>
              <img src={BgReceivingPlace} alt="" className={styles.footHeadingIcon} />
              発見報告所
            </h2>
            <p className={styles.footText}>
              クリア後に発見報告所に行くと、実物のクリアカードがもらえるよ。
            </p>
            {article?.receivingPlace && (
              <ul className={styles.receivingPlaceList}>
                {article?.receivingPlace.map((place, index) => (
                  <li key={index} className={styles.receivingPlaceItem}>
                    {index >= 1 && (
                      <p className={styles.footText}>上記施設がお休みの場合はここに来てね。</p>
                    )}
                    <h3
                      className={styles.receivingPlaceTitle}
                      dangerouslySetInnerHTML={{ __html: place.name }}
                    />
                    <div className={styles.receivingPlaceDetails}>
                      <div className={styles.receivingPlaceDetailsFirst}>
                        {place.address && (
                          <p className={styles.receivingPlaceDetail}>
                            <img
                              src={AddressReceivingPlace}
                              alt=""
                              className={styles.receivingPlaceIcon}
                            />
                            <span dangerouslySetInnerHTML={{ __html: place.address }} />
                          </p>
                        )}
                        {place.tel && (
                          <p className={styles.receivingPlaceDetail}>
                            <img
                              src={TelReceivingPlace}
                              alt=""
                              className={styles.receivingPlaceIcon}
                            />
                            {place.tel}
                          </p>
                        )}
                        {place.regularClosingDay && (
                          <p className={styles.receivingPlaceDetail}>
                            <img
                              src={DayReceivingPlace}
                              alt=""
                              className={styles.receivingPlaceIcon}
                            />
                            定休日：
                            <span dangerouslySetInnerHTML={{ __html: place.regularClosingDay }} />
                          </p>
                        )}
                        {place.receptionHours && (
                          <p className={styles.receivingPlaceDetail}>
                            <img src={TimeIcon} alt="" className={styles.receivingPlaceIcon} />
                            {`報告受付時間： ${place.receptionHours}`}
                          </p>
                        )}
                      </div>
                      <div className={styles.receivingPlaceDetailsSecond}>
                        {place.mapUrl && (
                          <a
                            href={place.mapUrl}
                            target="_blank"
                            className={styles.receivingPlaceMapUrl}
                            rel="noreferrer"
                          >
                            <img
                              src={mapReceivingPlace}
                              alt="Map"
                              className={styles.receivingPlaceMapUrlIcon}
                            />
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div ref={storesOfferTips} className={styles.storesOfferTips}>
            <h2 className={[styles.footHeading, styles.storesOfferTipsHeading].join(' ')}>
              <img src={BgStoresOfferTips} alt="" className={styles.footHeadingIcon} />
              ヒント提供店
            </h2>
            <p className={styles.footText}>
              手がかりを全て入手しても謎が解けないときは立ち寄ってみよう！解き方のヒントが貼ってあるよ。
            </p>
            {article?.storesOfferTips && (
              <ul className={styles.storesOfferTipsList}>
                {article?.storesOfferTips.map((store, index) => (
                  <li key={index} className={styles.storesOfferTipsItem}>
                    {store.name && (
                      <h3
                        className={styles.storesOfferTipsTitle}
                        dangerouslySetInnerHTML={{ __html: store.name }}
                      />
                    )}
                    <div className={styles.storesOfferTipsDetails}>
                      <div className={styles.storesOfferTipsDetailsFirst}>
                        {/* NOTE: 第二弾はどこでも全てのヒントを提供してもらえるためトルツメ
                        {store.clue?.length !== 0 && (
                          <p className={styles.storesOfferTipsClueText}>
                            {store.clue?.includes('1') && <img src={Clue1} alt="手がかり1" />}
                            {store.clue?.includes('2') && <img src={Clue2} alt="手がかり2" />}
                            {store.clue?.includes('3') && <img src={Clue3} alt="手がかり3" />}
                            のヒントがもらえます
                          </p>
                        )} */}
                        {store.address && (
                          <p className={styles.storesOfferTipsDetail}>
                            <img
                              src={AddressStoresOfferTips}
                              alt=""
                              className={styles.storesOfferTipsIcon}
                            />
                            <span dangerouslySetInnerHTML={{ __html: store.address }} />
                          </p>
                        )}
                        {store.tel && (
                          <p className={styles.storesOfferTipsDetail}>
                            <img
                              src={TelStoresOfferTips}
                              alt=""
                              className={styles.storesOfferTipsIcon}
                            />
                            {store.tel}
                          </p>
                        )}
                        {store.category && (
                          <p className={styles.storesOfferTipsDetail}>
                            <img
                              src={TagStoresOfferTips}
                              alt=""
                              className={styles.storesOfferTipsIcon}
                            />
                            {store.category}
                          </p>
                        )}
                      </div>
                      <div className={styles.storesOfferTipsDetailsSecond}>
                        {store.mapUrl && (
                          <a href={store.mapUrl} target="_blank" rel="noreferrer">
                            <img
                              src={mapStoresOfferTips}
                              alt="Map"
                              className={styles.mapStoresOfferTips}
                            />
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* 改修後 */}
          {article &&
            article?.parkingLot &&
            article?.parkingLot[0]?.clue?.length !== 0 &&
            article.parkingLot.length > 0 && (
              <div className={styles.parkingLot2}>
                <h2 className={[styles.footHeading, styles.parkingLotHeading].join(' ')}>
                  <img src={BgParkingLot01} alt="" className={styles.footHeadingIcon} />
                  駐車場
                </h2>
                <ul className={styles.parkingLot2List}>
                  {article?.parkingLot.map((store, index) => (
                    <li key={index} className={styles.parkingLot2Item}>
                      <>
                        <h3 className={styles.parkingLot2ListHeading}>
                          <img
                            src={BgParkingLot02}
                            alt=""
                            className={styles.parkingLot2HeadingIcon}
                          />
                          {store.parkingLot}
                        </h3>
                        <div className={styles.parkingLot2ListDetails}>
                          <div className={styles.parkingLot2ListDetailsFirst}>
                            {store.clue?.length !== 0 && (
                              <p className={styles.parkingLot2ClueText}>
                                {store.clue?.includes('1') && <img src={Clue1} alt="手がかり1" />}
                                {store.clue?.includes('2') && <img src={Clue2} alt="手がかり2" />}
                                {store.clue?.includes('3') && <img src={Clue3} alt="手がかり3" />}
                                を探すときに使おう
                              </p>
                            )}
                            {store.address && (
                              <p className={styles.parkingLot2Detail}>
                                <img
                                  src={AddressStoresOfferTips}
                                  alt=""
                                  className={styles.parkingLot2DetailsIcon}
                                />
                                <span dangerouslySetInnerHTML={{ __html: store.address }} />
                              </p>
                            )}
                          </div>
                          <div className={styles.parkingLot2ListDetailsSecond}>
                            {store.mapUrl && (
                              <a href={store.mapUrl} target="_blank" rel="noreferrer">
                                <img
                                  src={mapStoresOfferTips}
                                  alt="Map"
                                  className={styles.mapStoresOfferTips}
                                />
                              </a>
                            )}
                          </div>
                        </div>
                      </>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          {/* 改修前 */}
          {article &&
            article?.parkingLot &&
            article?.parkingLot[0]?.clue &&
            article?.parkingLot[0]?.clue?.length === 0 && (
              <div className={styles.parkingLot}>
                <ul className={styles.parkingLotList}>
                  {article?.parkingLot.map((store, index) => (
                    <li key={index} className={styles.parkingLotItem}>
                      <>
                        <img src={BgParkingLot03} alt="" className={styles.parkingLotIcon} />
                        {store.parkingLot}
                      </>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          <div className={styles.recommended}>
            <h2 className={[styles.footHeading, styles.recommendedHeading].join(' ')}>
              <img src={BgRecommended} alt="" className={styles.footHeadingIcon} />
              イチ押し！観光情報
            </h2>
            {article?.recommended && (
              <ul className={styles.recommendedList}>
                {article?.recommended.map((item, index) => (
                  <li key={index} className={styles.recommendedItem}>
                    <div className={styles.recommendedImageBox}>
                      <img
                        src={
                          getRecommendedFileName(index) &&
                          require(`components/pages/Area/images/${getRecommendedFileName(index)}`)
                        }
                        alt=""
                      />
                    </div>
                    <h3
                      className={styles.recommendedTitle}
                      dangerouslySetInnerHTML={{ __html: item.name }}
                    />
                    <p
                      className={styles.recommendedText}
                      dangerouslySetInnerHTML={{ __html: String(item.description) }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* イチ押し！観光情報 */}
        </div>
      </div>
      {isOpen && (
        <MapDialogView
          isOpen={isOpen}
          handleClose={handleClose}
          getClueMapFileName={getClueMapFileName}
        />
      )}
    </div>
  );
};
